import { Link } from "react-router-dom";
import { Check, ChevronRight, Award, Lock } from "lucide-react";
import { PACKAGES } from "../data/staticData";
import Reveal from "./Reveal";
import PermitAddOnSelector from "./PermitAddOnSelector";
import { useAddOns } from "./AddOnsContext";

export default function PermitPackages() {
  const { selected: selectedAddons, toggle: toggleAddon } = useAddOns();

  const addonsQuery = selectedAddons.length
    ? `&addons=${encodeURIComponent(selectedAddons.join(","))}`
    : "";

  return (
    <section
      id="permit-packages"
      data-testid="permit-packages-section"
      className="bg-[#0F0F0F] border-y border-white/10 py-20"
    >
      <div className="container-fld">
        <div className="max-w-3xl mb-12">
          <div className="overline mb-4">// Permit Packages</div>
          <h2 className="font-tactical text-4xl md:text-5xl font-bold uppercase tracking-tight text-white">
            Multi-State Concealed Carry
          </h2>
          <p className="mt-4 text-zinc-400 text-base md:text-lg leading-relaxed">
            Get certified in one state — or build coverage across multiple. Every
            package includes safety fundamentals, legal overview, and a completion
            certificate. Train once, carry across borders.
          </p>
        </div>

        <div
          className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6"
          data-testid="permit-packages-grid"
        >
          {PACKAGES.map((pkg, i) => {
            const isComingSoon = pkg.available === false;
            return (
            <Reveal key={pkg.id} delay={i * 80}>
              <div
                data-testid={`package-card-${pkg.id}`}
                className={`tactical-card group relative h-full flex flex-col ${
                  isComingSoon
                    ? "opacity-70 grayscale-[40%]"
                    : pkg.popular
                      ? "border-[#7CFF00]/60 shadow-[0_0_36px_rgba(124,255,0,0.18)]"
                      : ""
                }`}
              >
                {pkg.popular && !isComingSoon && (
                  <div
                    className="absolute -top-px left-0 right-0 bg-[#7CFF00] text-black text-center py-1 font-tactical text-xs uppercase tracking-widest flex items-center justify-center gap-1.5"
                    data-testid={`package-popular-${pkg.id}`}
                  >
                    <Award className="w-3 h-3" /> Most Comprehensive
                  </div>
                )}
                {isComingSoon && (
                  <div
                    data-testid={`package-coming-soon-${pkg.id}`}
                    className="absolute -top-px left-0 right-0 bg-zinc-800 text-zinc-300 text-center py-1 font-tactical text-xs uppercase tracking-[0.25em] flex items-center justify-center gap-1.5 border-b border-white/10"
                  >
                    <Lock className="w-3 h-3" /> Coming Soon
                  </div>
                )}

                <div
                  className={`aspect-[4/3] overflow-hidden bg-[#0A0A0A] ${
                    pkg.popular || isComingSoon ? "mt-7" : ""
                  }`}
                >
                  <img
                    src={pkg.image}
                    alt={pkg.name}
                    className={`w-full h-full object-cover transition-transform duration-700 ${
                      isComingSoon ? "" : "group-hover:scale-110"
                    }`}
                  />
                </div>

                <div className="p-6 flex flex-col flex-1">
                  <div className="flex gap-1.5 mb-3 flex-wrap">
                    {pkg.states.map((s) => (
                      <span
                        key={s}
                        className={`font-mono-tactical text-[10px] tracking-widest uppercase px-2 py-1 border ${
                          isComingSoon
                            ? "border-white/15 text-zinc-400"
                            : "border-[#7CFF00]/40 text-[#7CFF00] bg-[#7CFF00]/5"
                        }`}
                      >
                        {s}
                      </span>
                    ))}
                  </div>

                  <h3 className="font-tactical text-xl uppercase text-white font-bold leading-tight">
                    {pkg.name}
                  </h3>
                  <p className="overline mt-1 text-[10px]">{pkg.tagline}</p>

                  <ul className="mt-4 space-y-2 flex-1">
                    {pkg.features.map((f) => (
                      <li
                        key={f}
                        className="flex items-start gap-2 text-xs text-zinc-300 leading-relaxed"
                      >
                        <Check className={`w-3.5 h-3.5 mt-0.5 flex-shrink-0 ${
                          isComingSoon ? "text-zinc-500" : "text-[#7CFF00]"
                        }`} />
                        <span>{f}</span>
                      </li>
                    ))}
                  </ul>

                  <div className="mt-5 pt-5 border-t border-white/10 flex flex-col gap-3">
                    <div
                      className="flex items-baseline justify-between"
                      data-testid={`package-price-${pkg.id}`}
                    >
                      <span className="overline text-[10px]">
                        {isComingSoon ? "Launching At" : "Starting At"}
                      </span>
                      <span
                        className={`font-tactical text-3xl md:text-4xl font-bold tracking-tight ${
                          isComingSoon
                            ? "text-zinc-400"
                            : pkg.popular
                              ? "text-[#7CFF00] glow-text"
                              : "text-[#7CFF00]"
                        }`}
                      >
                        ${pkg.price}
                      </span>
                    </div>
                    {isComingSoon ? (
                      <button
                        type="button"
                        disabled
                        data-testid={`package-book-${pkg.id}`}
                        className="font-tactical uppercase tracking-widest text-xs py-3 text-center flex items-center justify-center gap-2 border border-white/20 text-zinc-400 bg-zinc-900/40 cursor-not-allowed"
                      >
                        <Lock className="w-3.5 h-3.5" /> Currently Unavailable
                      </button>
                    ) : (
                      <Link
                        to={`/booking?course=${encodeURIComponent(pkg.name)}${addonsQuery}`}
                        data-testid={`package-book-${pkg.id}`}
                        className={`font-tactical uppercase tracking-widest text-xs py-3 text-center transition-all flex items-center justify-center gap-1 ${
                          pkg.popular
                            ? "bg-[#7CFF00] text-black hover:bg-[#65CC00]"
                            : "border border-[#7CFF00] text-[#7CFF00] hover:bg-[#7CFF00] hover:text-black"
                        }`}
                      >
                        {pkg.cta} <ChevronRight className="w-3.5 h-3.5" />
                      </Link>
                    )}
                  </div>
                </div>
              </div>
            </Reveal>
            );
          })}
        </div>

        {/* Interactive add-on selector (replaces static notice) */}
        <Reveal delay={320}>
          <PermitAddOnSelector
            selected={selectedAddons}
            onToggle={toggleAddon}
          />
        </Reveal>

        {/* Coming Soon strip */}
        <Reveal delay={400}>
          <div
            data-testid="packages-coming-soon"
            className="mt-12 flex items-center justify-center gap-3 text-center"
          >
            <span className="h-px w-12 bg-white/15" />
            <span className="overline text-[11px] tracking-[0.3em]">
              DC and Utah Courses Coming Soon
            </span>
            <span className="h-px w-12 bg-white/15" />
          </div>
        </Reveal>
      </div>
    </section>
  );
}
